import { mapGetters, mapActions } from 'vuex';
import useVuelidate from '@vuelidate/core';

import {
  account as accountRule,
  required,
  email,
  phone,
  maxValue,
  numeric,
} from '@/utils/validators';
import {
  USER_LEVEL,
  USER_LEVEL_MAPPING,
  MEMBER_MODE,
} from '@/utils/constants';
import { GET_DEPARTMENT_LIST } from '@/store/setting/constants';

export default {
  name: 'MemberForm',
  props: {
    value: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    isDuplicated: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editEmail: false,
      roleMapping: USER_LEVEL_MAPPING,
      departments: [],
    };
  },
  async mounted() {
    this.departments = await this.getDepartmentList();
  },
  computed: {
    member: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    isOwner() {
      return this.member.role === USER_LEVEL.OWNER;
    },
    enabledRoles() {
      const roles = Object.values(USER_LEVEL).filter((l) => l < this.currentRole);

      return roles;
    },
    filteredRoleMapping() {
      const ownerAvailable = this.isOwner && this.isEditMode;

      if (!ownerAvailable) {
        return this.roleMapping.filter((item) => item.key !== USER_LEVEL.OWNER);
      }
      return this.roleMapping;
    },
    nameFieldMessage() {
      return this.v$.member.member_detail.name?.$errors?.[0]?.$message || '';
    },
    accountFieldMessage() {
      if (this.isDuplicated) {
        return '帳號重複輸入';
      }
      const errorMsg = this.v$.member.account?.$errors?.[0]?.$message || '';
      if (this.isCreateMode && !errorMsg) {
        return '登入帳號限使用英文字母、數字或符號';
      }
      return errorMsg;
    },
    allowOrderOnSiteFieldMessage() {
      return this.v$.member.allow_order_on_site?.$errors?.[0]?.$message || '需為0或正整數字';
    },
    emailFieldMessage() {
      return this.v$.member.email?.$errors?.[0]?.$message || '';
    },
    extensionFieldMessage() {
      return this.v$.member.member_detail.ext?.$errors?.[0]?.$message || '';
    },
    mobileFieldMessage() {
      return this.v$.member.member_detail.phone?.$errors?.[0]?.$message || '';
    },
    isCreateMode() {
      return this.mode === MEMBER_MODE.CREATE;
    },
    isEditMode() {
      return this.mode === MEMBER_MODE.EDIT;
    },
    invalidAccount() {
      const target = this.v$.member.account;
      if (this.isDuplicated) {
        return true;
      }
      return target.$dirty && target.$invalid;
    },
    invalidAllowOrderOnSite() {
      const target = this.v$.member.allow_order_on_site;
      return target.$dirty && target.$invalid;
    },
    invalidName() {
      const target = this.v$.member.member_detail?.name;
      return target.$dirty && target.$invalid;
    },
    invalidEmail() {
      const target = this.v$.member.email;
      return target.$dirty && target.$invalid;
    },
    invalidExtension() {
      const target = this.v$.member.member_detail?.ext;
      return target.$dirty && target.$invalid;
    },
    invalidMobile() {
      const target = this.v$.member.member_detail?.phone;
      return target.$dirty && target.$invalid;
    },
    ...mapGetters('user', ['currentRole']),
  },
  watch: {
    member: {
      handler(value) {
        this.$emit('input', value);
      },
      deep: true,
    },
  },
  methods: {
    onRemove() {
      this.$emit('remove');
    },
    isRoleAvailable(newRole) {
      if (this.currentRole < this.member.role) {
        /* e.g. Admin can't assign new role for other admin */
        return false;
      }
      if (this.isOwner) {
        /* Ownership transition should be done in Setting  */
        return false;
      }
      return Object.values(USER_LEVEL).filter((l) => l <= this.currentRole).includes(newRole);
    },
    onInputNonDigit(event) {
      if (!/\d/.test(event.key)) {
        event.preventDefault();
      }
    },
    ...mapActions('setting', {
      getDepartmentList: GET_DEPARTMENT_LIST,
    }),
  },
  validations() {
    return {
      member: {
        account: {
          accountRule,
          required: required('帳號'),
        },
        allow_order_on_site: {
          numeric,
          required: required('可現場訂單次數'),
          maxValue: maxValue('可現場訂單次數', 9999),
        },
        email: {
          email,
        },
        member_detail: {
          name: { required: required('姓名') },
          ext: { phone },
          phone: { numeric },
        },
      },
    };
  },
};
